import type { MachineryDirectiveCompliance } from '@/shared/hooks/api/machineryDirectiveCompliance'
import type { OnboardingStep } from '../components/machineryDirectiveOnboardingTypes'
import {
  getMachineryDirectiveOnboardingSidebarStepId,
  type MachineryDirectiveOnboardingSidebarStepId,
} from '../components/machineryDirectiveOnboardingSteps'

export type DeriveFurthestOnboardingStepInput = {
  compliance: MachineryDirectiveCompliance | undefined
  hasAssignedEPlan: boolean
  typeBReviewComplete: boolean
  isTypeCReviewCompleted: boolean
  hasStandardsProgress: boolean
}

function isUserElevatedHighRiskLocked(
  compliance: MachineryDirectiveCompliance | undefined
): boolean {
  return (
    compliance?.highRiskCheckedAt != null &&
    compliance.isHighRisk === false &&
    Boolean(compliance.highRiskUserOverrideReason?.trim())
  )
}

function isAutomatedHighRiskLocked(
  compliance: MachineryDirectiveCompliance | undefined
): boolean {
  return (
    compliance?.highRiskCheckedAt != null &&
    compliance.isHighRisk === true &&
    !compliance.highRiskUserOverrideReason?.trim()
  )
}

export function deriveFurthestOnboardingStep(
  input: DeriveFurthestOnboardingStepInput
): OnboardingStep {
  const {
    compliance,
    hasAssignedEPlan,
    typeBReviewComplete,
    isTypeCReviewCompleted,
    hasStandardsProgress,
  } = input

  if (compliance == null) {
    return 'intro'
  }

  if (isUserElevatedHighRiskLocked(compliance)) {
    return 'user_high_risk_override_locked'
  }

  if (isAutomatedHighRiskLocked(compliance)) {
    return 'high_risk_result'
  }

  if (compliance.highRiskCheckedAt == null) {
    if (hasAssignedEPlan) {
      return 'high_risk'
    }
    return 'intro'
  }

  if (compliance.isPartlyCompletedMachine == null) {
    return 'machine_completion'
  }

  if (!hasStandardsProgress) {
    return 'machine_locations'
  }

  if (!typeBReviewComplete) {
    if (isTypeCReviewCompleted) {
      return 'type_b_questionnaire'
    }
    return 'standards'
  }

  return 'type_b_questionnaire'
}

export function deriveFurthestProgressSidebarStepId(
  input: DeriveFurthestOnboardingStepInput
): MachineryDirectiveOnboardingSidebarStepId {
  const furthestStep = deriveFurthestOnboardingStep(input)
  return getMachineryDirectiveOnboardingSidebarStepId(furthestStep)
}

export function isHighRiskLockedFromCompliance(
  compliance: MachineryDirectiveCompliance | undefined
): boolean {
  return (
    isUserElevatedHighRiskLocked(compliance) ||
    isAutomatedHighRiskLocked(compliance)
  )
}
